import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import {
  getReaAgents, runReaAgent,
  type ReaAgentsCatalog, type ReaAgentRunResult
} from "../../api/rea-client";

type Props = { userId: string };

export function CreatorReaAgents({ userId }: Props) {
  // A new session must not inherit another user's task or completed analysis.
  return <CreatorReaAgentsContent key={userId} userId={userId} />;
}

function CreatorReaAgentsContent({ userId }: Props) {
  const id = useId();
  const [catalog, setCatalog] = useState<ReaAgentsCatalog | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [reloadVersion, setReloadVersion] = useState(0);
  const [agentId, setAgentId] = useState("");
  const [task, setTask] = useState("");
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState("");
  const [notice, setNotice] = useState("");
  const [result, setResult] = useState<ReaAgentRunResult | null>(null);
  const activeRun = useRef<AbortController | null>(null);
  const runVersion = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    setCatalogLoading(true);
    setCatalogError("");
    setCatalog(null);
    getReaAgents(userId, controller.signal)
      .then(response => {
        if (controller.signal.aborted) return;
        setCatalog(response);
        setAgentId(response.profiles[0].id);
      })
      .catch(error => {
        if (controller.signal.aborted) return;
        setCatalogError(error instanceof Error ? error.message : "No se pudieron cargar los agentes.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setCatalogLoading(false);
      });
    return () => controller.abort();
  }, [userId, reloadVersion]);

  useEffect(() => () => {
    runVersion.current += 1;
    activeRun.current?.abort();
    activeRun.current = null;
  }, []);

  const selectedAgent = catalog?.profiles.find(profile => profile.id === agentId);
  const trimmedTask = task.trim();
  const taskTooLong = catalog !== null
    && new TextEncoder().encode(trimmedTask).byteLength > catalog.limits.maxTaskBytes;

  function clearRun(message = "") {
    runVersion.current += 1;
    activeRun.current?.abort();
    activeRun.current = null;
    setRunning(false);
    setResult(null);
    setRunError("");
    setNotice(message);
  }

  async function execute(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (activeRun.current || !catalog?.engine.configured || !selectedAgent || !trimmedTask || taskTooLong) return;
    const controller = new AbortController();
    const version = ++runVersion.current;
    activeRun.current = controller;
    setRunning(true);
    setResult(null);
    setRunError("");
    setNotice("");
    const isCurrent = () => !controller.signal.aborted && runVersion.current === version;
    try {
      const response = await runReaAgent(userId, { agentId, task: trimmedTask }, controller.signal);
      if (isCurrent()) setResult(response);
    } catch (error) {
      if (isCurrent()) {
        setRunError(error instanceof Error ? error.message : "No se pudo completar el análisis del agente.");
      }
    } finally {
      if (isCurrent()) {
        activeRun.current = null;
        setRunning(false);
      }
    }
  }

  return <section className="rea-agents" aria-labelledby={id + "-heading"}>
    <div className="rea-agents-heading">
      <div>
        <h3 id={id + "-heading"}>Agentes REA</h3>
        <p>Elige una especialidad para analizar y preparar mejoras en ONE CORE.</p>
      </div>
      {catalog && <span className="rea-agent-engine-state">
        {result ? "El motor local ha respondido" : catalog.engine.configured
          ? "Motor local configurado" : "Motor local sin configurar"}
      </span>}
    </div>

    {catalogLoading && <p className="rea-agent-status" role="status">Cargando agentes…</p>}
    {catalogError && <div className="rea-agent-load-error">
      <p role="alert">{catalogError}</p>
      <div className="rea-actions">
        <button type="button" onClick={() => setReloadVersion(value => value + 1)}>Volver a cargar</button>
      </div>
    </div>}

    {catalog && <form onSubmit={event => void execute(event)}>
      <label htmlFor={id + "-profile"}>Especialidad
        <select id={id + "-profile"} value={agentId} aria-describedby={id + "-description"}
          onChange={event => { clearRun(); setAgentId(event.target.value); }}>
          {catalog.profiles.map(profile =>
            <option key={profile.id} value={profile.id}>{profile.displayName}</option>)}
        </select>
      </label>
      <p id={id + "-description"} className="rea-agent-help">{selectedAgent?.description}</p>
      <label htmlFor={id + "-task"}>¿Qué quieres que analice?
        <textarea id={id + "-task"} rows={4} data-writing="literal" value={task}
          placeholder="Describe el fallo o la mejora y añade las evidencias que tengas."
          aria-describedby={id + "-task-help" + (taskTooLong ? " " + id + "-length-error" : "")}
          aria-invalid={taskTooLong}
          onChange={event => { clearRun(); setTask(event.target.value); }} />
      </label>
      <p id={id + "-task-help"} className="rea-agent-help">Indica qué ocurre, qué esperabas y en qué apartado de OMNISYNIA.</p>
      {taskTooLong && <p id={id + "-length-error"} className="rea-agent-error" role="alert">
        La tarea es demasiado larga. Acórtala para poder enviarla.
      </p>}
      {!result && <p className="rea-agent-connection">
        {catalog.engine.configured
          ? "La conexión con el motor se comprobará al ejecutar."
          : "Activa el motor local de ONE CORE para ejecutar un agente."}
      </p>}
      <div className="rea-actions">
        <button type="submit" disabled={running || !catalog.engine.configured || !selectedAgent || !trimmedTask || taskTooLong}>
          {running ? "Analizando…" : "Ejecutar agente"}
        </button>
        {running && <button type="button" onClick={() => clearRun("Solicitud cancelada.")}>Cancelar</button>}
      </div>
    </form>}

    {running && <p className="rea-agent-status" role="status">El agente está analizando la tarea…</p>}
    {notice && <p className="rea-agent-status" role="status">{notice}</p>}
    {runError && <p className="rea-agent-error" role="alert">{runError}</p>}
    {result && <section className="rea-agent-result" aria-labelledby={id + "-result-heading"}>
      <h4 id={id + "-result-heading"}>Análisis del agente</h4>
      <p className="rea-agent-result-name">{result.agentName}</p>
      <p className="rea-agent-scope" role="status">
        Propuesta pendiente de aplicar; no se han ejecutado pruebas ni cambiado archivos.
      </p>
      <pre className="rea-agent-result-text">{result.text}</pre>
    </section>}
  </section>;
}
